import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate, fmtTime, API } from "../lib/api";
import { toast } from "sonner";
import { UserCheck, UserX, LogOut, ChevronLeft, CalendarDays, ClipboardList, Users } from "lucide-react";
import PhotoAttach from "../components/PhotoAttach";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

const ROLE_LABEL_KEY = { RECEPTIONIST: "att_role_receptionist", HOUSEKEEPER: "att_role_housekeeper", SECURITY: "att_role_security", OTHER: "att_role_other" };

// Backend returns violation strings in a fixed English format; parse and re-render in user's language.
function translateViolation(user, v) {
    let m = /^Client informed at (\S+) — after (\S+) deadline$/.exec(v);
    if (m) return t(user, "att_violation_client_late", { at: m[1], deadline: m[2] });
    m = /^Replacement deployed at (\S+) — exceeds (\S+)h from deadline$/.exec(v);
    if (m) return t(user, "att_violation_replacement_late", { at: m[1], h: m[2] });
    return v;
}

function todayIso() {
    const d = new Date();
    const off = -d.getTimezoneOffset();
    // Convert to IST (+5:30) — use browser local for display; for the API we send an IST date. Server also computes IST-today.
    // Best: send today's date as YYYY-MM-DD in browser's local zone. Server treats it opaquely.
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}

export default function AttendancePage() {
    const { user } = useAuth();
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [date, setDate] = useState(todayIso());
    const [rows, setRows] = useState([]);
    const [rules, setRules] = useState(null);
    const [loading, setLoading] = useState(true);
    const [absentFor, setAbsentFor] = useState(null); // roster staff obj
    const [absentForm, setAbsentForm] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, att, r] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get(`/sites/${siteId}/attendance`, { params: { date } }),
                api.get(`/sites/${siteId}/absence-rules`),
            ]);
            setSite(s.data);
            setRows(att.data);
            setRules(r.data);
        } catch (e) {
            toast.error(apiError(e));
        } finally { setLoading(false); }
    }, [siteId, date]);

    useEffect(() => { load(); }, [load]);

    async function markPresent(staffId) {
        try {
            await api.post(`/sites/${siteId}/attendance`, {
                staff_id: staffId, date,
                status: "PRESENT",
                check_in: new Date().toISOString(),
            });
            toast.success(t(user, "att_toast_checked_in"));
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function markCheckout(rec) {
        try {
            await api.post(`/sites/${siteId}/attendance`, {
                ...rec,
                status: "PRESENT",
                staff_id: rec.staff_id,
                date,
                check_in: rec.check_in,
                check_out: new Date().toISOString(),
            });
            toast.success(t(user, "att_toast_checked_out"));
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    function openAbsent(staff) {
        setAbsentFor(staff);
        setAbsentForm({
            cover_arranged: false,
            cover_person_name: "",
            absence_reason: "",
            client_informed_at: "",
            replacement_deployed_at: "",
        });
    }

    async function submitAbsent() {
        try {
            const payload = {
                staff_id: absentFor.id, date, status: "ABSENT",
                absence_reason: absentForm.absence_reason,
                cover_arranged: !!absentForm.cover_arranged,
                cover_person_name: absentForm.cover_arranged ? absentForm.cover_person_name : null,
                client_informed_at: absentForm.client_informed_at || null,
                replacement_deployed_at: absentForm.replacement_deployed_at || null,
            };
            const { data } = await api.post(`/sites/${siteId}/attendance`, payload);
            if (data.violations?.length) {
                const msg = data.violations.map((v) => translateViolation(user, v)).join("; ");
                toast.error(t(user, "att_toast_violation", { msg }));
            } else {
                toast.success(t(user, "att_toast_absence_recorded"));
            }
            setAbsentFor(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    const roleRequired = (role) => (rules?.required_roles || []).includes(role);

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to={`/sites/${siteId}/run`} className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3" data-testid="attendance-back-to-run">
                <ChevronLeft className="w-3 h-3" /> {t(user, "att_back_to_run")}
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{t(user, "att_title")}</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]"
                        data-testid="attendance-date-input"
                    />
                    <Link to={`/sites/${siteId}/roster`} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-600 hover:text-[#1E3355]" data-testid="attendance-roster-link">
                        <Users className="w-3.5 h-3.5" /> {t(user, "att_roster")}
                    </Link>
                    <Link to={`/sites/${siteId}/attendance/month`} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-600 hover:text-[#1E3355]" data-testid="attendance-monthly-link">
                        <CalendarDays className="w-3.5 h-3.5" /> {t(user, "att_monthly")}
                    </Link>
                </div>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden" data-testid="attendance-list">
                {loading ? (
                    <div className="p-6 text-center text-sm text-gray-500">{t(user, "loading")}</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-500" data-testid="attendance-empty">
                        {t(user, "att_no_roster")} <Link to={`/sites/${siteId}/roster`} className="text-[#1E3355] font-medium">{t(user, "att_add_staff")}</Link>.
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {rows.map(({ staff, attendance }) => (
                            <li key={staff.id} className="p-4 sm:p-5" data-testid={`attendance-row-${staff.name}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-display font-semibold text-[#1E3355]">{staff.name}</div>
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#F4F6F9] text-[#1E3355] border border-gray-200">
                                                {t(user, ROLE_LABEL_KEY[staff.role] || "att_role_other")}
                                            </span>
                                            {roleRequired(staff.role) && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">{t(user, "att_required")}</span>
                                            )}
                                        </div>
                                        {attendance && attendance.status === "PRESENT" && (
                                            <div className="mt-1 text-xs text-emerald-700 font-medium">
                                                {t(user, "att_present_time", { in: fmtTime(attendance.check_in), outPart: attendance.check_out ? t(user, "att_out_suffix", { out: fmtTime(attendance.check_out) }) : "" })}
                                            </div>
                                        )}
                                        {attendance && attendance.status === "ABSENT" && (
                                            <div className="mt-1 text-xs text-[#E02B2B] font-medium">
                                                {attendance.cover_arranged
                                                    ? t(user, "att_absent_line_cover", { reason: attendance.absence_reason, cover: attendance.cover_person_name })
                                                    : t(user, "att_absent_line_no_cover", { reason: attendance.absence_reason })}
                                                {attendance.violations?.length > 0 && (
                                                    <div className="mt-1 text-[11px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid={`violation-${staff.name}`}>
                                                        ⚠ {attendance.violations.map((v) => translateViolation(user, v)).join("; ")}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {!attendance && (
                                            <>
                                                <button onClick={() => markPresent(staff.id)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`checkin-${staff.name}`}>
                                                    <UserCheck className="w-3.5 h-3.5" /> {t(user, "att_check_in")}
                                                </button>
                                                <button onClick={() => openAbsent(staff)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-white border border-red-200 text-[#E02B2B] hover:bg-red-50" data-testid={`mark-absent-${staff.name}`}>
                                                    <UserX className="w-3.5 h-3.5" /> {t(user, "att_absent")}
                                                </button>
                                            </>
                                        )}
                                        {attendance && attendance.status === "PRESENT" && !attendance.check_out && (
                                            <button onClick={() => markCheckout(attendance)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-[#1E3355] hover:bg-[#15243C] text-white" data-testid={`checkout-${staff.name}`}>
                                                <LogOut className="w-3.5 h-3.5" /> {t(user, "att_check_out")}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {attendance && attendance.status === "PRESENT" && (
                                    <div className="mt-2">
                                        <PhotoAttach
                                            parentType="attendance"
                                            parentId={attendance.id}
                                            siteId={siteId}
                                            tag="check-in"
                                            compact
                                            testIdPrefix={`attendance-${staff.name}-photo`}
                                        />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {absentFor && (
                <AbsentModal
                    user={user}
                    staff={absentFor}
                    rules={rules}
                    form={absentForm}
                    setForm={setAbsentForm}
                    onClose={() => setAbsentFor(null)}
                    onSubmit={submitAbsent}
                />
            )}
        </div>
    );
}

function AbsentModal({ user, staff, rules, form, setForm, onClose, onSubmit }) {
    const required = (rules?.required_roles || []).includes(staff.role);
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="absent-modal">
            <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
                <div className="p-5 border-b border-gray-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#E02B2B]">{t(user, "att_mark_absent")}</div>
                    <div className="font-display font-semibold text-[#1E3355] mt-1">{staff.name} · {t(user, ROLE_LABEL_KEY[staff.role] || "att_role_other")}</div>
                    {required && (
                        <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            {t(user, "att_contract_role_note")}
                        </div>
                    )}
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <Field label={t(user, "att_reason_required")}>
                        <textarea value={form.absence_reason || ""} onChange={(e) => setForm({ ...form, absence_reason: e.target.value })} rows={2} required className={fieldCls} data-testid="absent-reason" />
                    </Field>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t(user, "att_cover_arranged")}</label>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setForm({ ...form, cover_arranged: true })} className={`flex-1 py-2 rounded-md text-sm font-medium border ${form.cover_arranged ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-gray-600 border-gray-200"}`} data-testid="absent-cover-yes">{t(user, "att_yes")}</button>
                            <button type="button" onClick={() => setForm({ ...form, cover_arranged: false })} className={`flex-1 py-2 rounded-md text-sm font-medium border ${form.cover_arranged === false ? "bg-[#1E3355] text-white border-[#1E3355]" : "bg-white text-gray-600 border-gray-200"}`} data-testid="absent-cover-no">{t(user, "att_no")}</button>
                        </div>
                    </div>
                    {form.cover_arranged && (
                        <Field label={t(user, "att_cover_name_required")}>
                            <input value={form.cover_person_name || ""} onChange={(e) => setForm({ ...form, cover_person_name: e.target.value })} required className={fieldCls} data-testid="absent-cover-name" />
                        </Field>
                    )}
                    {required && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">{t(user, "att_contractual_fields")}</div>
                            <Field label={t(user, "att_client_informed_at", { t: rules.client_inform_deadline_time })}>
                                <input type="time" value={form.client_informed_at || ""} onChange={(e) => setForm({ ...form, client_informed_at: e.target.value })} required className={fieldCls} data-testid="absent-client-informed" />
                            </Field>
                            <Field label={t(user, "att_replacement_at", { h: rules.replacement_deadline_hours })}>
                                <input type="time" value={form.replacement_deployed_at || ""} onChange={(e) => setForm({ ...form, replacement_deployed_at: e.target.value })} required className={fieldCls} data-testid="absent-replacement-time" />
                            </Field>
                        </div>
                    )}
                </div>
                <div className="flex border-t border-gray-100">
                    <button onClick={onClose} className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50" data-testid="absent-cancel">{t(user, "cancel")}</button>
                    <button onClick={onSubmit} className="flex-1 py-3 text-sm font-semibold text-white bg-[#E02B2B] hover:bg-[#C92525]" data-testid="absent-submit">{t(user, "att_record_absence")}</button>
                </div>
            </div>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) {
    return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>);
}
